import { useState, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Navbar from '../home/components/Navbar';
import UserFormModal from './components/UserFormModal';
import { getUsers, updateUserStatus, deleteUser, type User, type UserStatus } from '../../api/user';
import { usePermission } from '../../hooks/usePermission';

/**
 * 用户状态 → 徽章样式。
 * 键必须与后端 Party.status 的真实值域一致（激活 / 停用 / 禁用）——
 * 此前用的是 active / 启用 / disabled / locked 一套臆想值，真实状态全部落到灰色兜底。
 */
const STATUS_STYLES: Record<string, string> = {
  激活: 'bg-emerald-50 text-emerald-600',
  停用: 'bg-gray-100 text-gray-500',
  禁用: 'bg-red-50 text-red-600',
};

function statusStyle(status: string) {
  return STATUS_STYLES[status] ?? 'bg-gray-100 text-gray-500';
}

/**
 * 当前状态下可执行的一次流转。
 *
 * 「禁用」是管理员管制动作，后端强制要求同时提交原因，本页不提供入口（由后台管理端负责）；
 * 这里只做无需原因的 激活 ↔ 停用，以及把已被禁用的账号恢复为激活
 * （恢复时后端会一并清空历史 stop_reason）。
 */
function nextStatus(status: string): { to: UserStatus; label: string; icon: string } {
  if (status === '激活') {
    return { to: '停用', label: '停用账号', icon: 'ri-lock-line' };
  }
  return {
    to: '激活',
    label: status === '禁用' ? '解除禁用' : '恢复激活',
    icon: 'ri-lock-unlock-line',
  };
}

/**
 * 用户管理页（仅管理员可见完整功能）。
 * 能力：关键字搜索（后端模糊匹配姓名/用户名/手机/邮箱）、分页、
 *       停用 / 恢复激活、删除用户、新建用户。
 */
export default function UsersPage() {
  const { isAdmin } = usePermission();

  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 12;
  const [showCreate, setShowCreate] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<User | null>(null);
  const [actionError, setActionError] = useState('');

  // 关键字防抖
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedKeyword(keyword.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [keyword]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-users', debouncedKeyword, page],
    queryFn: () => getUsers({ page, pageSize, keyword: debouncedKeyword || undefined }),
    enabled: isAdmin,
  });

  // 状态切换
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: UserStatus }) => updateUserStatus(id, status),
    onSuccess: () => {
      setActionError('');
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => setActionError(err instanceof Error ? err.message : '操作失败'),
  });

  // 删除用户
  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteUser(id),
    onSuccess: () => {
      setActionError('');
      setConfirmDelete(null);
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    },
    onError: (err) => {
      setConfirmDelete(null);
      setActionError(err instanceof Error ? err.message : '删除失败');
    },
  });

  const handleToggleStatus = useCallback(
    (u: User) => {
      statusMutation.mutate({ id: u.id, status: nextStatus(u.status).to });
    },
    [statusMutation],
  );

  // 非管理员：无权限提示
  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#f8fafa]">
        <Navbar />
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-16">
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-16 h-16 flex items-center justify-center bg-amber-50 rounded-2xl mb-4">
              <i className="ri-lock-2-line text-amber-400 text-3xl" />
            </div>
            <p className="text-base font-medium text-gray-700 mb-1">需要管理员权限</p>
            <p className="text-sm text-gray-400">用户管理仅对管理员开放，如需操作请联系系统管理员</p>
          </div>
        </div>
      </div>
    );
  }

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-[#f8fafa]">
      <Navbar />

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-5 md:py-8 pb-24 md:pb-8">
        {/* 页头 */}
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-6 md:mb-8">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900">用户管理</h1>
            <p className="text-sm text-gray-400 mt-1">
              系统注册用户一览，共 <span className="text-teal-600 font-medium">{total}</span> 位用户
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-3 md:px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-user-add-line" />
            </div>
            <span>新建用户</span>
          </button>
        </div>

        {/* 搜索栏 */}
        <div className="relative w-full sm:max-w-sm mb-5 md:mb-6">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center pointer-events-none">
            <i className="ri-search-line text-gray-400 text-sm" />
          </div>
          <input
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="搜索姓名、用户名、手机、邮箱..."
            className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-200 rounded-xl bg-white focus:outline-none focus:border-teal-400 focus:ring-1 focus:ring-teal-400 transition-colors"
          />
        </div>

        {actionError && (
          <div className="flex items-center gap-2 px-3 py-2.5 mb-4 text-sm text-red-600 bg-red-50 rounded-xl">
            <i className="ri-error-warning-line" />
            <span>{actionError}</span>
          </div>
        )}

        {/* 内容区 */}
        {isLoading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-100 p-5 animate-pulse">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-gray-100 rounded-full" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 bg-gray-100 rounded w-3/4" />
                    <div className="h-3 bg-gray-100 rounded w-1/2" />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-100 rounded w-full" />
                  <div className="h-3 bg-gray-100 rounded w-2/3" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-full mb-3">
              <i className="ri-error-warning-line text-red-400 text-2xl" />
            </div>
            <p className="text-sm text-gray-500 mb-4">
              {error instanceof Error ? error.message : '加载失败'}
            </p>
            <button
              type="button"
              onClick={() => refetch()}
              className="px-4 py-2 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 cursor-pointer transition-colors"
            >
              重新加载
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24">
            <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-2xl mb-4">
              <i className="ri-team-line text-teal-300 text-3xl" />
            </div>
            <p className="text-base font-medium text-gray-600 mb-1">
              {debouncedKeyword ? '没有匹配的用户' : '暂无注册用户'}
            </p>
            <p className="text-sm text-gray-400">{debouncedKeyword ? '尝试调整搜索关键字' : '新用户注册后将显示在这里'}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-4">
              {items.map((u) => (
                <div key={u.id} className="bg-white rounded-xl border border-gray-100 p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 shrink-0 flex items-center justify-center bg-teal-50 rounded-full">
                      <i className="ri-user-3-line text-teal-500" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-gray-900 truncate">
                          {u.fullName || u.username}
                        </span>
                        {u.isAdmin && (
                          <span className="px-1.5 py-0.5 text-[10px] rounded bg-teal-50 text-teal-600 shrink-0">管理员</span>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 truncate">@{u.username}</p>
                    </div>
                    <span
                      className={`px-2 py-0.5 text-[10px] rounded-full shrink-0 ${statusStyle(u.status)}`}
                      title={u.stopReason ? `原因：${u.stopReason}` : undefined}
                    >
                      {u.status}
                    </span>
                  </div>

                  <div className="space-y-1.5 text-xs text-gray-500 mb-4">
                    {u.phone && (
                      <div className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-phone-line text-gray-300" />
                        </div>
                        <span className="truncate">{u.phone}</span>
                      </div>
                    )}
                    {u.email && (
                      <div className="flex items-center gap-2">
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className="ri-mail-line text-gray-300" />
                        </div>
                        <span className="truncate">{u.email}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className="ri-time-line text-gray-300" />
                      </div>
                      <span className="truncate">
                        注册于 {u.createdAt ? new Date(u.createdAt).toLocaleDateString('zh-CN') : '-'}
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
                    <button
                      type="button"
                      onClick={() => handleToggleStatus(u)}
                      disabled={statusMutation.isPending}
                      className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 text-xs rounded-lg border border-gray-200 text-gray-600 hover:border-teal-300 hover:text-teal-600 cursor-pointer transition-colors disabled:opacity-50"
                    >
                      <i className={nextStatus(u.status).icon} />
                      {nextStatus(u.status).label}
                    </button>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(u)}
                      disabled={deleteMutation.isPending}
                      className="flex items-center justify-center w-8 h-8 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 cursor-pointer transition-colors disabled:opacity-50"
                      title="删除用户"
                    >
                      <i className="ri-delete-bin-4-line text-sm" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* 分页 */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between pt-6 mt-2 border-t border-gray-100">
                <span className="text-sm text-gray-400">
                  共 <span className="text-gray-700 font-medium">{total}</span> 位用户，第{' '}
                  <span className="text-gray-700 font-medium">{page}</span>/{totalPages} 页
                </span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-left-s-line text-sm" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-500 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed cursor-pointer transition-colors"
                  >
                    <i className="ri-arrow-right-s-line text-sm" />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* 新建用户弹窗 */}
      <UserFormModal open={showCreate} onClose={() => setShowCreate(false)} onSuccess={() => refetch()} />

      {/* 删除确认弹窗 */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setConfirmDelete(null)} />
          <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-sm p-5">
            <div className="w-10 h-10 flex items-center justify-center bg-red-50 rounded-full mb-3">
              <i className="ri-delete-bin-4-line text-red-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 mb-1">确认删除用户</h3>
            <p className="text-sm text-gray-500 mb-5">
              将删除「{confirmDelete.fullName || confirmDelete.username}」，该操作不可恢复。
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2.5 text-sm text-gray-600 border border-gray-200 rounded-xl hover:border-teal-300 cursor-pointer transition-colors"
              >
                取消
              </button>
              <button
                type="button"
                onClick={() => deleteMutation.mutate(confirmDelete.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2.5 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-xl cursor-pointer transition-colors disabled:opacity-50"
              >
                {deleteMutation.isPending ? '删除中...' : '确认删除'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
